export interface PriceBreakdownInput {
  /** Raw EPEX spot price, €/kWh excl. VAT. */
  spotPrice: number;
  /** Provider markup, €/kWh excl. VAT. */
  perKwhMarkup: number;
  /** Multiplier applied to the spot price only (BE contracts like "EPEX × 1,02"). Default 1. */
  spotCoefficient?: number;
  /** Energy tax / accijnzen, €/kWh excl. VAT. */
  energyTaxRate: number;
  /** Country VAT rate, e.g. 0.21 or 0.06. */
  vatRate: number;
  includeTaxes: boolean;
}

export interface PriceBreakdown {
  spotCost: number;
  markup: number;
  energyTax: number;
  vat: number;
  total: number;
}

export interface MonthlyPriceEstimateInput extends PriceBreakdownInput {
  usageKwh: number;
  /** Fixed monthly provider fee. Provider configuration stores this incl. VAT. */
  monthlyFee: number;
}

export interface MonthlyPriceEstimate {
  perKwhPrice: number;
  /** Components of `perKwhPrice`; VAT is listed separately. */
  breakdown: PriceBreakdown;
  energyCost: number;
  monthlyFee: number;
  total: number;
}

export function computePriceBreakdown(input: PriceBreakdownInput): PriceBreakdown {
  const spotCost = input.spotPrice * (input.spotCoefficient ?? 1);
  const markup = input.perKwhMarkup;
  const energyTax = input.includeTaxes ? input.energyTaxRate : 0;
  const vatBase = spotCost + markup + energyTax;
  const vat = input.includeTaxes ? vatBase * input.vatRate : 0;
  return { spotCost, markup, energyTax, vat, total: vatBase + vat };
}

export function computeMonthlyPriceEstimate(
  input: MonthlyPriceEstimateInput
): MonthlyPriceEstimate {
  const breakdown = computePriceBreakdown(input);
  const perKwhPrice = breakdown.total;
  const energyCost = input.usageKwh * perKwhPrice;

  return {
    perKwhPrice,
    breakdown,
    energyCost,
    monthlyFee: input.monthlyFee,
    total: energyCost + input.monthlyFee,
  };
}

const roundingFormatters = new Map<number, Intl.NumberFormat>();

/**
 * Rounds `value` to `decimals` places exactly like the UI formatter does: `Intl.NumberFormat`
 * rounds the shortest decimal form of the number half away from zero (1.005 → 1.01), where
 * `toFixed` would round its binary value (1.005 → 1.00). Using the same engine guarantees
 * that an amount rounded here prints as the same digits.
 */
export function roundTo(value: number, decimals: number): number {
  let formatter = roundingFormatters.get(decimals);
  if (!formatter) {
    formatter = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: false,
    });
    roundingFormatters.set(decimals, formatter);
  }
  return Number(formatter.format(value));
}

export interface DisplayedMonthlyEstimateInput extends MonthlyPriceEstimateInput {
  /** Decimals shown for per-kWh amounts. Default 4. */
  perKwhDecimals?: number;
}

/** The lines of an estimate, as the UI prints them. */
export interface DisplayedMonthlyEstimate {
  /** Market price per kWh (incl. VAT when taxes are included), rounded as shown. */
  marketPrice: number;
  /** Spot factor as shown (at most 4 decimals); 1 when the contract has none. */
  spotCoefficient: number;
  /** Energy taxes per kWh incl. VAT, rounded as shown; 0 when taxes are excluded. */
  energyTax: number;
  /** Supplier markup per kWh (incl. VAT when taxes are included), rounded as shown. */
  markup: number;
  /** marketPrice × spotCoefficient + energyTax + markup, not rounded. */
  perKwhPrice: number;
  usageKwh: number;
  /** perKwhPrice × usageKwh, rounded to cents. */
  energyCost: number;
  /** Fixed monthly fee, rounded to cents. */
  monthlyFee: number;
  /** energyCost + monthlyFee. */
  total: number;
}

/** Rounds half away from zero, like the UI's number formatter. */
const roundHalfAway = (value: number) => Math.sign(value) * Math.round(Math.abs(value));

/**
 * Computes the monthly estimate from exactly the numbers the UI prints: per-kWh amounts
 * rounded to `perKwhDecimals`, the factor to at most 4 decimals. The energy cost is that
 * running calculation (price × factor + taxes + markup) × usage, rounded only once to cents,
 * so a reader who recomputes the printed lines arrives at the printed amounts. Integer
 * arithmetic keeps floating-point noise out of it. At 4 decimals the total stays within
 * about 4.5 cent of the exact `computeMonthlyPriceEstimate` total.
 */
export function computeDisplayedMonthlyEstimate(
  input: DisplayedMonthlyEstimateInput
): DisplayedMonthlyEstimate {
  const decimals = input.perKwhDecimals ?? 4;
  const scale = 10 ** decimals;
  const vatFactor = input.includeTaxes ? 1 + input.vatRate : 1;
  const marketPrice = roundTo(input.spotPrice * vatFactor, decimals);
  const spotCoefficient = roundTo(input.spotCoefficient ?? 1, 4);
  const energyTax = input.includeTaxes
    ? roundTo(input.energyTaxRate * vatFactor, decimals)
    : 0;
  const markup = roundTo(input.perKwhMarkup * vatFactor, decimals);

  // Per kWh in units of 1/(scale × 10⁴) euro: market × factor exactly, plus taxes and markup.
  const perKwhUnits =
    Math.round(marketPrice * scale) * Math.round(spotCoefficient * 10_000) +
    (Math.round(energyTax * scale) + Math.round(markup * scale)) * 10_000;
  const energyCents = roundHalfAway((perKwhUnits * input.usageKwh) / (scale * 100));
  const monthlyFee = roundTo(input.monthlyFee, 2);
  const totalCents = energyCents + Math.round(monthlyFee * 100);
  return {
    marketPrice,
    spotCoefficient,
    energyTax,
    markup,
    perKwhPrice: perKwhUnits / (scale * 10_000),
    usageKwh: input.usageKwh,
    energyCost: energyCents / 100,
    monthlyFee,
    total: totalCents / 100,
  };
}

export interface DisplayedFeedInInput {
  /** Raw spot price, €/kWh excl. VAT; omit when no market price is known. */
  spotPrice?: number;
  /** Multiplier on the spot price for fed-in energy. Absent = 1. */
  feedInSpotCoefficient?: number;
  /** Fee per kWh excl. VAT; negative = credit on top of the market price. */
  feedInPerKwhCharge: number;
  /** VAT applied to feed-in amounts for display (0 when exempt). */
  vatRate: number;
  /** Decimals shown for per-kWh amounts. Default 4. */
  perKwhDecimals?: number;
}

/** Feed-in revenue per kWh as the UI prints it: `spotCoefficient × marketPrice − fee`. */
export interface DisplayedFeedIn {
  /** Spot factor, only when it differs from 1. */
  spotCoefficient?: number;
  /** Fee per kWh incl. the display VAT; negative = credit. */
  fee: number;
  /** Market price per kWh incl. the display VAT; null when no spot price is known. */
  marketPrice: number | null;
  /** `spotCoefficient × marketPrice − fee`; null when no spot price is known. */
  revenue: number | null;
}

export function computeDisplayedFeedIn(input: DisplayedFeedInInput): DisplayedFeedIn {
  const decimals = input.perKwhDecimals ?? 4;
  const vatFactor = 1 + input.vatRate;
  const spotCoefficient =
    input.feedInSpotCoefficient !== undefined && input.feedInSpotCoefficient !== 1
      ? input.feedInSpotCoefficient
      : undefined;
  const fee = roundTo(input.feedInPerKwhCharge * vatFactor, decimals);
  if (input.spotPrice === undefined) {
    return { spotCoefficient, fee, marketPrice: null, revenue: null };
  }
  const marketPrice = roundTo(input.spotPrice * vatFactor, decimals);
  const spotTerm =
    spotCoefficient === undefined
      ? marketPrice
      : roundTo(marketPrice * spotCoefficient, decimals);
  return {
    spotCoefficient,
    fee,
    marketPrice,
    revenue: roundTo(spotTerm - fee, decimals),
  };
}
